import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { distinctUntilChanged, map } from 'rxjs';
import { CompanyContactsPanelComponent } from './company-contacts-panel.component';

/**
 * Tela "Contatos da empresa" do usuário COMPANY (`/dashboard/company/contacts`, menu Gerenciamento).
 *
 * O usuário da empresa não enxerga a listagem nem o cadastro de empresas (são do tenant), então
 * esta é a porta dele para o mesmo `CompanyContactsPanelComponent`. A empresa é a selecionada no
 * topo da plataforma: trocar de empresa troca a lista. As escritas exigem `CompanyContact.MANAGE`
 * (ou as permissões do cadastro de empresas) e o backend confere que a empresa está no escopo do
 * usuário; sem elas, o painel fica só para leitura.
 */
@Component({
  selector: 'app-company-contacts-page',
  standalone: true,
  imports: [SharedModule, CompanyContactsPanelComponent],
  template: `
    <section class="contacts-page">
      <app-breadcrumb [home]="breadcrumbHome" [items]="breadcrumbs" />

      <header class="contacts-page__header">
        <h1>Contatos da empresa</h1>
        <p>
          Quem da empresa recebe cada comunicação da plataforma, como o Relatório de Risco e os
          e-mails do Data Room.
        </p>
      </header>

      @if (companyId()) {
        <div class="contacts-page__card">
          <app-company-contacts-panel [companyId]="companyId()" />
        </div>
      } @else {
        <p class="contacts-page__empty">
          Escolha uma empresa no topo da tela para ver os contatos dela.
        </p>
      }
    </section>
  `,
  styles: [
    `
      .contacts-page {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1rem 1.5rem 2rem;
      }

      .contacts-page__header h1 {
        margin: 0;
        font-size: 1.375rem;
        font-weight: 600;
        color: var(--text-color);
      }

      .contacts-page__header p {
        margin: 0.25rem 0 0;
        font-size: 0.875rem;
        color: var(--text-color-secondary);
      }

      .contacts-page__card {
        background: var(--surface-card, #fff);
        border: 1px solid var(--surface-border);
        border-radius: 0.75rem;
        padding: 1rem 1.25rem;
      }

      .contacts-page__empty {
        margin: 0;
        font-size: 0.875rem;
        color: var(--text-color-secondary);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyContactsPageComponent {
  private readonly store = inject(Store<AppState>);

  protected readonly breadcrumbHome: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: 'Dashboard',
  };
  protected readonly breadcrumbs: MenuItem[] = [{ label: 'Contatos da empresa' }];

  protected readonly companyId = toSignal(
    this.store.select(selectCompanyId).pipe(
      map(id => (typeof id === 'string' && id.trim() ? id : null)),
      distinctUntilChanged()
    ),
    { initialValue: null }
  );
}
